import { Box, Button, Portal, Text, VStack, Icon, useBreakpointValue, HStack, Table, Tbody, Tr, Td } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { FaFastBackward, FaStepBackward, FaPlay, FaStepForward, FaFastForward, FaStickyNote, FaEyeSlash, FaMapMarker, FaRandom, FaVolumeDown } from 'react-icons/fa';
import { FiShare2, FiDownload, FiInfo, FiPlus } from 'react-icons/fi';
import { TbRepeat } from 'react-icons/tb';

interface MobileInfoOverlayProps {
  onDismiss: () => void;
  onDontShowAgain: () => void;
  hasLoadedFiles: boolean;
  readyPlayers: Set<number>;
  force?: boolean;
  onShown?: () => void;
}

const KeyBox = ({ children }: { children: React.ReactNode }) => (
  <Box
    as="span"
    px={2}
    py={0.5}
    bg="whiteAlpha.200"
    borderRadius="md"
    fontSize="sm"
    border="1px solid"
    borderColor="whiteAlpha.300"
    display="inline-block"
  >
    {children}
  </Box>
);

// Mono/Stereo Icon Component for tutorial (shows stereo - two overlapping circles)
const MonoIconTutorial = ({ size = 20 }: { size?: number }) => {
  const iconSize = size;
  const strokeWidth = 2;
  
  return (
    <svg
      width={iconSize}
      height={iconSize}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Left circle */}
      <circle
        cx="14"
        cy="20"
        r="12"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill="none"
      />
      {/* Right circle */}
      <circle
        cx="26"
        cy="20"
        r="12"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill="none"
      />
    </svg>
  );
};

// Filter Icon Component for tutorial
const FilterIconTutorial = ({ size = 20 }: { size?: number }) => {
  const iconSize = size;
  // Match stereo icon stroke width visually
  // Stereo icon uses strokeWidth=2 in 24x24 viewBox
  // Using a slightly thicker stroke width
  const strokeWidth = 22;
  
  return (
    <svg
      width={iconSize}
      height={iconSize}
      viewBox="0 0 411.5 411.5"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g transform="translate(205.75, 205.75) scale(0.85) translate(-205.75, -205.75)">
        <path d="M214.04,0l17.18,1.57c90.11,10.85,163.77,82.08,177.7,171.76l2.58,24.17v16.5l-1.56,17.19c-10.92,92.51-86.35,167.72-178.71,178.75l-17.18,1.56h-16.5l-17.18-1.56C87.76,398.78,12.75,323.87,1.65,231.18L.08,214.5c.19-5.81-.26-11.7,0-17.5C4.92,90.23,90.87,4.66,197.54,0h16.5ZM391.5,163C374.6,83.36,303.26,22.12,222.28,15.5,60.9,2.31-43.99,183.47,50.17,316.16c1.36,1.91,9.18,12.81,10.65,12.85l72.91-163.84c.48-.47,3.63-2.17,4.08-2.17h253.7ZM143.61,178.06l-71.87,163.2c28.75,29.08,67.91,48.31,108.63,53.67,118.3,15.56,221.31-79.19,216.16-198.2l-2.03-19.23-250.89.56Z"/>
      </g>
    </svg>
  );
};

// Dim Icon Component for tutorial (volume down icon)
const DimIconTutorial = ({ size = 20 }: { size?: number }) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z"
        fill="currentColor"
      />
    </svg>
  );
};

const steps = [
  {
    title: "Player Switching",
    titleIcon: null,
    description: "Each player contains an audio file.\n\n<customIcon> Use the Switcher button to cycle selection or tap the player you'd like to switch to directly\n\nTo reorder players, press and hold on one of them, then drag it up or down in the list",
    customIcon: (
      <Box
        width="24px"
        height="24px"
        minWidth="24px"
        minHeight="24px"
        borderRadius="full"
        border="2px solid"
        borderColor="accent.500"
        display="flex"
        alignItems="center"
        justifyContent="center"
        color="accent.400"
        fontSize="md"
        fontWeight="bold"
        flexShrink={0}
      >
        1
      </Box>
    ),
    randomIcon: (
      <Box
        width="24px"
        height="24px"
        minWidth="24px"
        minHeight="24px"
        maxWidth="24px"
        maxHeight="24px"
        borderRadius="full"
        border="2px solid"
        borderColor="accent.500"
        display="flex"
        alignItems="center"
        justifyContent="center"
        color="accent.400"
        fontSize="md"
        fontWeight="bold"
        lineHeight="1"
        overflow="hidden"
      >
        ?
      </Box>
    )
  },
  {
    title: "Transport Controls",
    description: "<transportTable>",
    transportControls: [
      { icon: FaFastBackward, description: "Back 10sec (Hold to return to 0:00)" },
      { icon: FaStepBackward, description: "Back 3sec (Hold to return to previous playback position)" },
      { icon: FaPlay, description: "Play/pause" },
      { icon: FaStepForward, description: "Forward 3sec" },
      { icon: FaFastForward, description: "Forward 10sec" }
    ]
  },
  {
    title: "Monitoring",
    description: "<transportTable>",
    transportControls: [
      { customIcon: <DimIconTutorial size={20} />, description: "Dim master volume" },
      { customIcon: <MonoIconTutorial size={20} />, description: "Cycle stereo/mono/sides listening modes" },
      { customIcon: <FilterIconTutorial size={20} />, description: "Master filter controls (highpass, lowpass, bandpass)" },
      { description: "Double tap to toggle current filter on/off" }
    ]
  },
  {
    title: "Blind Test Modes",
    titleIcon: "<Icon as={FaEyeSlash} />",
    titleIconPosition: "left",
    description: "• Basic: simple switch-and-reveal\n• Pick Fave: find out which track you really prefer — Compare (scores) or Narrow Down (knockout to a winner)\n• Guess Track: how good are your ears?\n\n<blindModeIcon> Each player can be removed from the Blind Mode testing pool with this toggle button",
    blindModeIcon: (
      <Icon as={FaEyeSlash} color="accent.400" boxSize="20px" />
    )
  },
  {
    title: "",
    titleIcon: null,
    description: "Compare sections at different points on the timeline.\n\nManual Sync\n\n<customIcon1> Set an anchor point for each track (e.g. beat 1 of the chorus).\n\n<customIcon2> Sync anchor points.\n\n<customIcon3> Press again to reset sync.\n\n\nAuto-Sync\n\n<customIcon3> Press without first setting anchor points (experimental!).",
    customIcon1: (
      <Box position="relative" color="gray.400">
        <FaMapMarker size={24} />
        <Text
          position="absolute"
          top="42%"
          left="50%"
          transform="translate(-50%, -50%)"
          fontSize="14px"
          fontWeight="bold"
          color="black"
        >
          A
        </Text>
      </Box>
    ),
    customIcon2: (
      <Box position="relative" color="gray.300" opacity={0.8}>
        <FaMapMarker size={24} />
        <Text
          position="absolute"
          top="42%"
          left="50%"
          transform="translate(-50%, -50%)"
          fontSize="14px"
          fontWeight="bold"
          color="black"
        >
          S
        </Text>
      </Box>
    ),
    customIcon3: (
      <Box position="relative" color="accent.400">
        <FaMapMarker size={24} />
        <Text
          position="absolute"
          top="42%"
          left="50%"
          transform="translate(-50%, -50%)"
          fontSize="14px"
          fontWeight="bold"
          color="black"
        >
          S
        </Text>
      </Box>
    ),
    customIcon: null
  },
  {
    title: "Looping",
    titleIcon: "<Icon as={TbRepeat} />",
    titleIconPosition: "left",
    description: "Tap to set the loop start point\nTap again to set the loop end point\nTap once more to clear the loop",
    icon: null
  },
  {
    title: "Import, Sharing & Notes",
    description: "<sharingIcons>",
    sharingItems: [
      { 
        icon: FiPlus, 
        color: "blue.400", 
        description: "Import additional files" 
      },
      { 
        icon: FiShare2, 
        color: "blue.400", 
        description: "If you load files from Dropbox or Google Drive, they can be shared with friends." 
      },
      { 
        icon: FaStickyNote, 
        color: "blue.400", 
        description: "Add time-stamped text notes that will be included in your shared setups." 
      },
      { 
        icon: FiDownload, 
        color: "blue.400", 
        description: "If \"Allow Downloads\" is checked during setup sharing, all files can be downloaded." 
      }
    ]
  }
];

const MobileInfoOverlay = ({ onDismiss, onDontShowAgain, hasLoadedFiles, readyPlayers, force = false, onShown }: MobileInfoOverlayProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasShownBefore, setHasShownBefore] = useState(false);
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    if (force) {
      setHasShownBefore(false);
      setCurrentStep(0);
    }
  }, [force]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let retryTimeoutId: NodeJS.Timeout;

    if (!isMobile) {
      setIsVisible(false);
      return;
    }
    
    const hasShown = localStorage.getItem('mobileInfoOverlayShown');
    
    if (!force && hasShown === 'true') {
      setIsVisible(false);
      return;
    }

    if (hasLoadedFiles && readyPlayers.has(1) && (!hasShownBefore || force)) {
      let retryCount = 0;
      const maxRetries = 10;
      const retryInterval = 100;

      const checkForElements = () => {
        const playerElement = document.querySelector('div[data-player-number="1"]');
        const switchButton = document.querySelector('[data-testid="player-switch-button"]');
        
        if (playerElement && switchButton) {
          setIsVisible(true);
          if (!force) {
            setHasShownBefore(true);
          }
          onShown?.();
          return;
        }

        if (retryCount < maxRetries) {
          retryCount++;
          retryTimeoutId = setTimeout(checkForElements, retryInterval);
        }
      };

      timeoutId = setTimeout(checkForElements, 500);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (retryTimeoutId) clearTimeout(retryTimeoutId);
    };
  }, [hasLoadedFiles, readyPlayers, hasShownBefore, force, onShown, isMobile]);

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss();
  };

  const handleDontShowAgain = () => {
    localStorage.setItem('mobileInfoOverlayShown', 'true');
    setIsVisible(false);
    onDontShowAgain();
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleDismiss();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isVisible) {
    return null;
  }

  const currentStepData = steps[currentStep];

  return (
    <Portal>
      <Box
        position="fixed"
        inset={0}
        bg="rgba(0, 0, 0, 0.8)"
        zIndex={999}
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={4}
      >
        <Box
          bg="gray.900"
          borderRadius="xl"
          maxW="100%"
          w="90%"
          maxH="90vh"
          border="1px solid"
          borderColor="rgba(255, 165, 0, 0.5)"
          boxShadow="dark-lg"
          display="flex"
          flexDirection="column"
          h="calc(70vh + 60px)"
          overflow="hidden"
        >
          {/* Header */}
          <Box py={4} px={6}>
            <HStack spacing={2} justify="space-between">
              <HStack spacing={2}>
                {currentStepData.titleIcon && currentStepData.titleIconPosition === "left" && (
                  <Box>
                    {currentStepData.titleIcon.split('\n').map((line, i) => {
                      if (line.includes('<Icon')) {
                        const parts = line.split(/<Icon as=\{|\} \/>/);
                        return parts.map((part, k) => {
                          if (part.startsWith('Fa') || part.startsWith('Fi') || part.startsWith('Tb')) {
                            const IconComponent = {
                              FaFastBackward,
                              FaStepBackward,
                              FaPlay,
                              FaStepForward,
                              FaFastForward,
                              FaStickyNote,
                              FaEyeSlash,
                              FiShare2,
                              FiDownload,
                              TbRepeat
                            }[part];
                            return <Icon key={k} as={IconComponent} boxSize="24px" />;
                          }
                          return null;
                        });
                      }
                      return null;
                    })}
                  </Box>
                )}
                {currentStepData.title && (
                  <Text fontSize="xl" fontWeight="bold" color="white">
                    {currentStepData.title.includes('Import, Sharing & Notes') 
                      ? (document.querySelector('[data-testid="add-files-button"]') !== null 
                         ? 'Import, Sharing & Notes' 
                         : 'Sharing & Notes')
                      : currentStepData.title}
                  </Text>
                )}
                {currentStepData.titleIcon && currentStepData.titleIconPosition !== "left" && (
                  <Box>
                    {currentStepData.titleIcon.split('\n').map((line, i) => {
                      if (line.includes('<Icon')) {
                        const parts = line.split(/<Icon as=\{|\} \/>/);
                        return parts.map((part, k) => {
                          if (part.startsWith('Fa') || part.startsWith('Fi') || part.startsWith('Tb')) {
                            const IconComponent = {
                              FaFastBackward,
                              FaStepBackward,
                              FaPlay,
                              FaStepForward,
                              FaFastForward,
                              FaStickyNote,
                              FaEyeSlash,
                              FiShare2,
                              FiDownload,
                              TbRepeat
                            }[part];
                            return <Icon key={k} as={IconComponent} boxSize="24px" />;
                          }
                          return null;
                        });
                      }
                      return null;
                    })}
                  </Box>
                )}
              </HStack>
              <Text color="whiteAlpha.700" fontSize="sm">
                {currentStep + 1}/{steps.length}
              </Text>
            </HStack>
          </Box>
          
          {/* Content area - flex="1" makes it take up all available space */}
          <Box flex="1" overflowY="auto" px={6} pb={4}>
            <Box color="white" fontSize="md">
              {currentStepData.description.split('\n').map((line, i) => {
                if (line.includes('<customIcon>')) {
                  const [before, after] = line.split('<customIcon>');
                  return (
                    <Box key={i} display="flex" alignItems="center" gap={2} mb={2}>
                      <Text as="span">{before}</Text>
                      {currentStepData.customIcon}
                      <Text as="span">{after}</Text>
                    </Box>
                  );
                }
                if (line.includes('<randomIcon>')) {
                  const [before, after] = line.split('<randomIcon>');
                  return (
                    <Box key={i} display="flex" alignItems="center" gap={2} mb={2}>
                      <Text as="span">{before}</Text>
                      {currentStepData.randomIcon}
                      <Text as="span">{after}</Text>
                    </Box>
                  );
                }
                if (line.includes('<blindModeIcon>')) {
                  const [before, after] = line.split('<blindModeIcon>');
                  return (
                    <Box key={i} display="flex" alignItems="center" gap={2} mb={2}>
                      <Text as="span">{before}</Text>
                      {currentStepData.blindModeIcon}
                      <Text as="span">{after}</Text>
                    </Box>
                  );
                }
                if (line.includes('<customIcon1>')) {
                  const [before, after] = line.split('<customIcon1>');
                  return (
                    <Box key={i} display="flex" alignItems="center" gap={2} mb={2}>
                      <Text as="span">{before}</Text>
                      {currentStepData.customIcon1}
                      <Text as="span">{after}</Text>
                    </Box>
                  );
                }
                if (line.includes('<customIcon2>')) {
                  const [before, after] = line.split('<customIcon2>');
                  return (
                    <Box key={i} display="flex" alignItems="center" gap={2} mb={2}>
                      <Text as="span">{before}</Text>
                      {currentStepData.customIcon2}
                      <Text as="span">{after}</Text>
                    </Box>
                  );
                }
                if (line.includes('<customIcon3>')) {
                  const [before, after] = line.split('<customIcon3>');
                  return (
                    <Box key={i} display="flex" alignItems="center" gap={2} mb={2}>
                      <Text as="span">{before}</Text>
                      {currentStepData.customIcon3}
                      <Text as="span">{after}</Text>
                    </Box>
                  );
                }
                if (line.includes('<transportTable>')) {
                  return (
                    <Box key={i} w="100%">
                      <Table variant="simple" size="sm">
                        <Tbody>
                          {currentStepData.transportControls?.map((control, idx) => (
                            <Tr key={idx} borderBottom="none">
                              <Td p={1} w="40px" borderBottom="none">
                                {'customIcon' in control && control.customIcon ? (
                                  <Box color="white" width="20px" height="20px" display="flex" alignItems="center" justifyContent="center">
                                    {control.customIcon}
                                  </Box>
                                ) : 'icon' in control && control.icon ? (
                                  <Icon as={control.icon} boxSize="20px" color="white" />
                                ) : null}
                              </Td>
                              <Td p={1} color="white" borderBottom="none">
                                {control.description}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  );
                }
                if (line.includes('<sharingIcons>')) {
                  const hasImportButton = document.querySelector('[data-testid="add-files-button"]') !== null;
                  const filteredItems = currentStepData.sharingItems?.filter((item, idx) => {
                    // First item is the import item, only show it if import button exists
                    if (idx === 0 && item.icon === FiPlus) {
                      return hasImportButton;
                    }
                    return true;
                  });
                  
                  return (
                    <Box key={i} w="100%">
                      <VStack spacing={3} align="start">
                        {filteredItems?.map((item, idx) => (
                          <HStack key={idx} spacing={3} align="start">
                            <Icon as={item.icon} boxSize="24px" color={item.color} />
                            <Text color="white" flex="1">{item.description}</Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  );
                }
                if (line.includes('<Icon')) {
                  const parts = line.split(/<Icon as=\{|\} \/>/);
                  return (
                    <Box key={i} display="flex" alignItems="center" gap={2} mb={2}>
                      {parts.map((part, k) => {
                        if (part.startsWith('Fa') || part.startsWith('Fi') || part.startsWith('Tb')) {
                          const IconComponent = {
                            FaFastBackward,
                            FaStepBackward,
                            FaPlay,
                            FaStepForward,
                            FaFastForward,
                            FaStickyNote,
                            FaEyeSlash,
                            FiShare2,
                            FiDownload,
                            TbRepeat
                          }[part];
                          return <Icon key={k} as={IconComponent} boxSize="24px" />;
                        }
                        return <Text as="span" key={k}>{part}</Text>;
                      })}
                    </Box>
                  );
                }
                // Handle subheadings
                if (line === "Manual Sync" || line === "Auto-Sync") {
                  return (
                    <Text key={i} mb={2} mt={3} fontWeight="bold" fontSize="md" display="block">
                      {line}
                    </Text>
                  );
                }
                // Handle empty lines
                if (line.trim() === '') {
                  return <Text key={i} mb={2} display="block">&nbsp;</Text>;
                }
                // Handle italic tags
                const italicParts = line.split(/<italic>|<\/italic>/);
                const formattedParts = italicParts.map((part, idx) => {
                  if (idx % 2 === 1) {
                    // Odd indices are italic content
                    return <Text as="span" key={`italic-${idx}`} fontStyle="italic">{part}</Text>;
                  }
                  return part;
                });
                
                return <Text as="span" key={i} mb={2} display="block">{formattedParts}</Text>;
              })}
            </Box>
          </Box>

          <Box 
            borderTop="1px" 
            borderColor="whiteAlpha.200"
            py={4} 
            px={6}
            bg="gray.900"
            borderBottomLeftRadius="xl"
            borderBottomRightRadius="xl"
          >
            <VStack spacing={2} align="center">
              <HStack spacing={2} w="100%" justify="center">
                <Button
                  variant="outline"
                  color="white"
                  size="sm"
                  onClick={handleBack}
                  _hover={{ bg: 'whiteAlpha.100' }}
                  fontSize="sm"
                  fontWeight="medium"
                  borderColor="orange.500"
                  isDisabled={currentStep === 0}
                >
                  Back
                </Button>
                <Button
                  variant="outline"
                  color="white"
                  size="sm"
                  onClick={handleNext}
                  _hover={{ bg: 'whiteAlpha.100' }}
                  fontSize="sm"
                  fontWeight="medium"
                  borderColor="orange.500"
                >
                  Next
                </Button>
              </HStack>
              <HStack spacing={2} w="100%" justify="center">
                <Button
                  variant="outline"
                  color="white"
                  size="sm"
                  onClick={handleDismiss}
                  _hover={{ bg: 'whiteAlpha.100' }}
                  fontSize="sm"
                  fontWeight="medium"
                  borderColor="#ACD5F3"
                >
                  Let&apos;s go!
                </Button>
                <Button
                  variant="outline"
                  color="white"
                  size="sm"
                  onClick={handleDontShowAgain}
                  _hover={{ bg: 'whiteAlpha.100' }}
                  fontSize="sm"
                  fontWeight="medium"
                  borderColor="#ACD5F3"
                >
                  Don&apos;t show again
                </Button>
              </HStack>
              <HStack spacing={2} color="whiteAlpha.600" fontSize="sm" justify="center" mt={1}>
                <Icon as={FiInfo} boxSize="16px" />
                <Text>Access this tutorial again via the info button</Text>
              </HStack>
            </VStack>
          </Box>
        </Box>
      </Box>
    </Portal>
  );
};

export default MobileInfoOverlay; 